/**
 * Marketplace API Service - интеграция с backend /marketplace endpoints
 * Event Master v1.5 - Backend Integration
 */

import { httpClient } from "../http-client.service";
import {
  Supplier,
  SupplierService,
  ServiceCategory,
  SupplierStatus,
  ApiResponse,
} from "../../types/shared-types";
import { extractApiData } from "./response-utils";

const BASE_URL = "/marketplace";

// ===== SUPPLIERS =====

export interface SupplierQuery {
  category?: ServiceCategory;
  status?: SupplierStatus;
  verified?: boolean;
  page?: number;
  limit?: number;
  query?: string; // текстовый поиск по имени/описанию
  city?: string; // фильтр по городу/локации
  minRating?: number; // минимальный рейтинг (0–5)
}

export const getSuppliers = async (
  params?: SupplierQuery,
): Promise<Supplier[]> => {
  const { data } = await httpClient.get<ApiResponse<Supplier[]>>(
    `${BASE_URL}/suppliers`,
    { params },
  );
  return data.data;
};

export const createSupplier = async (
  supplier: Partial<Supplier>,
): Promise<Supplier> => {
  const { data } = await httpClient.post<ApiResponse<Supplier>>(
    `${BASE_URL}/suppliers`,
    supplier,
  );
  return data.data;
};

export const getSupplier = async (id: string): Promise<Supplier> => {
  const { data } = await httpClient.get<ApiResponse<Supplier>>(
    `${BASE_URL}/suppliers/${id}`,
  );
  return data.data;
};

export const updateSupplier = async (
  id: string,
  supplier: Partial<Supplier>,
): Promise<Supplier> => {
  const { data } = await httpClient.put<ApiResponse<Supplier>>(
    `${BASE_URL}/suppliers/${id}`,
    supplier,
  );
  return data.data;
};

export const deleteSupplier = async (id: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/suppliers/${id}`);
};

// ===== SERVICES =====

export interface ServiceQuery {
  category?: ServiceCategory;
  supplierId?: string;
  available?: boolean;
}

export const getServices = async (
  params?: ServiceQuery,
): Promise<SupplierService[]> => {
  try {
    const { data } = await httpClient.get<
      ApiResponse<SupplierService[]> | SupplierService[]
    >(`${BASE_URL}/services`, { params });
    return Array.isArray(data) ? data : (data?.data ?? []);
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response
      ?.status;
    if (status === 404) return [];
    throw err;
  }
};

export const createService = async (
  service: Partial<SupplierService>,
): Promise<SupplierService> => {
  const { data } = await httpClient.post<ApiResponse<SupplierService>>(
    `${BASE_URL}/services`,
    service,
  );
  return data.data;
};

export const getService = async (id: string): Promise<SupplierService> => {
  const { data } = await httpClient.get<ApiResponse<SupplierService>>(
    `${BASE_URL}/services/${id}`,
  );
  return data.data;
};

export const updateService = async (
  id: string,
  service: Partial<SupplierService>,
): Promise<SupplierService> => {
  const { data } = await httpClient.put<ApiResponse<SupplierService>>(
    `${BASE_URL}/services/${id}`,
    service,
  );
  return data.data;
};

export const deleteService = async (id: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/services/${id}`);
};

// ===== QUOTATIONS =====

export interface Quotation {
  id: string;
  supplierId: string;
  serviceId: string;
  eventId: string;
  price: number;
  currency: string;
  description: string;
  validUntil: Date;
  status: "pending" | "accepted" | "rejected" | "expired";
  createdAt: Date;
  updatedAt: Date;
}

/** Request payload for creating a quotation — matches backend CreateQuotationDto */
export interface CreateQuotationRequest {
  title: string;
  supplierId?: string;
  eventId?: string;
  description?: string;
  requirements?: Record<string, unknown>;
  /** Number or range object { min, max } — stored as JSONB */
  budget?: number | Record<string, unknown>;
  eventDate?: string;
  guestCount?: number;
}

/**
 * Enriched quotation returned by backend (includes supplier data via enrichQuotation()).
 * All fields are optional except id, status, createdAt to accommodate evolving backend shape.
 */
export interface EnrichedQuotation {
  id: string;
  status: string;
  createdAt: string;
  supplierId?: string;
  eventId?: string;
  description?: string;
  requirements?: Record<string, unknown>;
  budget?: number | Record<string, unknown>;
  eventDate?: string;
  guestCount?: number;
  updatedAt?: string;
  title?: string;
  // Fields added by backend enrichQuotation()
  supplierName?: string;
  supplierRating?: number;
  service?: string;
  price?: number;
  currency?: string;
  deliveryTime?: string;
  validUntil?: string;
}

export interface QuotationQuery {
  userId?: string;
  supplierId?: string;
  eventId?: string;
  status?: string;
}

export const getQuotations = async (
  params?: QuotationQuery,
): Promise<EnrichedQuotation[]> => {
  const { data } = await httpClient.get<
    ApiResponse<EnrichedQuotation[]> | EnrichedQuotation[]
  >(`${BASE_URL}/quotations`, { params });
  return Array.isArray(data) ? data : (data?.data ?? []);
};

export const createQuotation = async (
  quotation: CreateQuotationRequest,
): Promise<EnrichedQuotation> => {
  const { data } = await httpClient.post<
    ApiResponse<EnrichedQuotation> | EnrichedQuotation
  >(`${BASE_URL}/quotations`, quotation);
  return (
    (data as ApiResponse<EnrichedQuotation>)?.data ??
    (data as EnrichedQuotation)
  );
};

export const getQuotation = async (id: string): Promise<Quotation> => {
  const { data } = await httpClient.get<ApiResponse<Quotation>>(
    `${BASE_URL}/quotations/${id}`,
  );
  return data.data;
};

export const updateQuotation = async (
  id: string,
  quotation: Partial<Quotation>,
): Promise<Quotation> => {
  const { data } = await httpClient.put<ApiResponse<Quotation>>(
    `${BASE_URL}/quotations/${id}`,
    quotation,
  );
  return data.data;
};

export const deleteQuotation = async (id: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/quotations/${id}`);
};

// ===== ORDERS =====

export interface Order {
  id: string;
  eventId: string;
  supplierId: string;
  serviceId: string;
  quotationId?: string;
  totalAmount?: number;
  totalPrice?: number;
  currency?: string;
  status: "pending" | "confirmed" | "in_progress" | "completed" | "cancelled";
  paymentStatus?: "pending" | "paid" | "refunded";
  orderDate?: Date;
  deliveryDate?: Date;
  eventDate?: Date;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  /** Enriched by GET /orders/:id */
  eventTitle?: string;
  clientName?: string;
  supplierName?: string;
  serviceName?: string;
}

export interface OrderQuery {
  eventId?: string;
  supplierId?: string;
  status?: string;
  paymentStatus?: string;
}

export const getOrders = async (params?: OrderQuery): Promise<Order[]> => {
  const { data } = await httpClient.get<ApiResponse<Order[]> | Order[]>(
    `${BASE_URL}/orders`,
    { params },
  );
  return Array.isArray(data) ? data : (data?.data ?? []);
};

export const createOrder = async (order: Partial<Order>): Promise<Order> => {
  const { data } = await httpClient.post<ApiResponse<Order>>(
    `${BASE_URL}/orders`,
    order,
  );
  return data.data;
};

export const getOrder = async (id: string): Promise<Order> => {
  const { data } = await httpClient.get<ApiResponse<Order> | Order>(
    `${BASE_URL}/orders/${id}`,
  );
  return (
    data && typeof data === "object" && "data" in data
      ? (data as ApiResponse<Order>).data
      : data
  ) as Order;
};

export const updateOrder = async (
  id: string,
  order: Partial<Order>,
): Promise<Order> => {
  const { data } = await httpClient.put<ApiResponse<Order>>(
    `${BASE_URL}/orders/${id}`,
    order,
  );
  return data.data;
};

export const deleteOrder = async (id: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/orders/${id}`);
};

// ===== SEARCH =====

export interface SearchQuery {
  query?: string;
  category?: ServiceCategory;
  location?: string;
  priceRange?: {
    min: number;
    max: number;
  };
  date?: Date;
  verified?: boolean;
}

export interface SearchResult {
  suppliers: Supplier[];
  services: SupplierService[];
  totalResults: number;
  searchTime: number;
}

export const searchMarketplace = async (
  params: SearchQuery,
): Promise<SearchResult> => {
  const { data } = await httpClient.post<ApiResponse<SearchResult>>(
    `${BASE_URL}/search`,
    params,
  );
  return data.data;
};

// ===== REVIEWS =====

export interface Review {
  id: string;
  supplierId: string;
  userId: string;
  orderId?: string;
  rating: number;
  title: string;
  comment: string;
  pros?: string[];
  cons?: string[];
  /**
   * AD-27. Сколько человек отметили отзыв полезным. Кэш над таблицей голосов,
   * который сервер ПЕРЕСЧИТЫВАЕТ из истины (ADR-013), а не инкрементирует, —
   * поэтому число здесь можно показывать как есть, не досчитывая на клиенте.
   * Колонка есть у сущности всегда, включая ответ на создание отзыва.
   */
  helpfulCount: number;
  /**
   * AD-27. Отмечал ли ЭТОТ отзыв тот, кто смотрит. Необязательно намеренно:
   * поле добавляют только чтения списка и одного отзыва; ответы на создание и
   * редактирование его не несут, и объявить его обязательным значило бы
   * соврать про их форму.
   */
  markedHelpfulByMe?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Фильтры GET /marketplace/reviews — дословно набор `@Query` контроллера
 * (Backend/src/modules/marketplace/controllers/marketplace.controller.ts).
 *
 * Поля `verified` в этом наборе нет и не было: сервер такой параметр не
 * читает, так что отправка его ничего не фильтровала. Взамен объявлены
 * `userId`, `limit` и `offset`, которые сервер читает, а тип раньше скрывал.
 */
export interface ReviewQuery {
  supplierId?: string;
  userId?: string;
  rating?: number;
  limit?: number;
  offset?: number;
}

export const getReviews = async (params?: ReviewQuery): Promise<Review[]> => {
  const { data } = await httpClient.get<ApiResponse<Review[]>>(
    `${BASE_URL}/reviews`,
    { params },
  );
  return data.data;
};

export const createReview = async (
  review: Partial<Review>,
): Promise<Review> => {
  const { data } = await httpClient.post<ApiResponse<Review>>(
    `${BASE_URL}/reviews`,
    review,
  );
  return data.data;
};

export const getReview = async (id: string): Promise<Review> => {
  const { data } = await httpClient.get<ApiResponse<Review>>(
    `${BASE_URL}/reviews/${id}`,
  );
  return data.data;
};

export const updateReview = async (
  id: string,
  review: Partial<Review>,
): Promise<Review> => {
  const { data } = await httpClient.put<ApiResponse<Review>>(
    `${BASE_URL}/reviews/${id}`,
    review,
  );
  return data.data;
};

export const deleteReview = async (id: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/reviews/${id}`);
};

export const replyToReview = async (
  reviewId: string,
  replyText: string,
): Promise<Review> => {
  const { data } = await httpClient.post<ApiResponse<Review>>(
    `${BASE_URL}/reviews/${reviewId}/reply`,
    { replyText },
  );
  return data.data;
};

/** AD-27. Ответ POST /marketplace/reviews/:id/helpful. */
export interface MarkReviewHelpfulResult {
  reviewId: string;
  helpfulCount: number;
  markedHelpfulByMe: boolean;
}

/**
 * AD-27. Отметить отзыв полезным.
 *
 * Ответ сервера НЕ отбрасывается: он несёт пересчитанный счётчик, и экрану
 * больше не нужно ни угадывать его инкрементом, ни перезапрашивать список.
 * Повтор безопасен — на сервере уникальный ключ пары (отзыв, пользователь) и
 * ON CONFLICT DO NOTHING, поэтому второй клик возвращает то же самое число.
 */
export const markReviewHelpful = async (
  reviewId: string,
): Promise<MarkReviewHelpfulResult> => {
  const { data } = await httpClient.post<ApiResponse<MarkReviewHelpfulResult>>(
    `${BASE_URL}/reviews/${reviewId}/helpful`,
  );
  return data.data;
};

// ===== FAVORITES =====

export interface Favorite {
  id: string;
  userId: string;
  supplierId?: string;
  serviceId?: string;
  createdAt: Date;
}

/** Supplier card data returned by GET /marketplace/suppliers/favorites (when implemented) */
export interface FavoriteSupplier {
  id: string;
  name: string;
  category?: string;
  location?: string;
  rating?: number;
  description?: string;
  services?: string[];
  verified?: boolean;
  addedAt: string;
  lastContacted?: string;
}

export const getFavorites = async (): Promise<Favorite[]> => {
  const { data } = await httpClient.get<ApiResponse<Favorite[]>>(
    `${BASE_URL}/favorites`,
  );
  return data.data;
};

/** GET /marketplace/suppliers/favorites — returns list of favorite suppliers. Backend may 404 until endpoint is implemented. */
export const getFavoriteSuppliers = async (): Promise<FavoriteSupplier[]> => {
  const res = await httpClient.get<
    FavoriteSupplier[] | ApiResponse<FavoriteSupplier[]>
  >(`${BASE_URL}/suppliers/favorites`);
  const body = (
    res as { data?: FavoriteSupplier[] | ApiResponse<FavoriteSupplier[]> }
  ).data;
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object" && "data" in body)
    return (body as ApiResponse<FavoriteSupplier[]>).data ?? [];
  return [];
};

export const addToFavorites = async (
  favorite: Partial<Favorite>,
): Promise<Favorite> => {
  const { data } = await httpClient.post<ApiResponse<Favorite>>(
    `${BASE_URL}/favorites`,
    favorite,
  );
  return data.data;
};

export const removeFromFavorites = async (id: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/favorites/${id}`);
};

/** DELETE /marketplace/suppliers/:supplierId/favorite — remove supplier from favorites by supplier id */
export const removeSupplierFromFavorites = async (
  supplierId: string,
): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/suppliers/${supplierId}/favorite`);
};

// ===== VERIFICATION =====
// W1-S4 (MKT-003): verifySupplier удалён — BE-роут POST /suppliers/:id/verify
// удалён (была заглушка). Админская верификация — supplier-profile/:id/verify.

// Волна S (MKT-003): suspendSupplier + SuspensionData удалены — BE-роут
// POST /suppliers/:id/suspend был заглушкой и удалён. Канонический путь
// приостановки — supplier-profile/:id/suspend (админский, реально меняет статус).

// ===== BOOKINGS =====

export interface Booking {
  id: string;
  userId: string;
  supplierId: string;
  serviceId: string;
  eventId?: string;
  bookingDate: Date;
  startDate: Date;
  endDate?: Date;
  totalAmount: number;
  currency: string;
  status: "pending" | "confirmed" | "in_progress" | "completed" | "cancelled";
  paymentStatus: "pending" | "paid" | "refunded";
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface BookingQuery {
  userId?: string;
  supplierId?: string;
  status?: string;
}

export const getBookings = async (
  params?: BookingQuery,
): Promise<Booking[]> => {
  const { data } = await httpClient.get<ApiResponse<Booking[]>>(
    `${BASE_URL}/bookings`,
    { params },
  );
  return data.data;
};

export const createBooking = async (
  booking: Partial<Booking>,
): Promise<Booking> => {
  const { data } = await httpClient.post<ApiResponse<Booking>>(
    `${BASE_URL}/bookings`,
    booking,
  );
  return data.data;
};

export const getBooking = async (id: string): Promise<Booking> => {
  const { data } = await httpClient.get<ApiResponse<Booking>>(
    `${BASE_URL}/bookings/${id}`,
  );
  return data.data;
};

export const updateBooking = async (
  id: string,
  booking: Partial<Booking>,
): Promise<Booking> => {
  const { data } = await httpClient.put<ApiResponse<Booking>>(
    `${BASE_URL}/bookings/${id}`,
    booking,
  );
  return data.data;
};

export const deleteBooking = async (id: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/bookings/${id}`);
};

// ===== SEARCH & COMPARE =====

export interface CompareParams {
  quotationIds: string[];
}

export interface ComparisonResult {
  quotations: EnrichedQuotation[];
  metrics: {
    bestPriceId: string | null;
    bestRatingId: string | null;
  };
}

export const compareItems = async (
  params: CompareParams,
): Promise<ComparisonResult> => {
  const { data } = await httpClient.post<
    ApiResponse<ComparisonResult> | ComparisonResult
  >(`${BASE_URL}/compare`, params);
  return (
    (data as ApiResponse<ComparisonResult>)?.data ?? (data as ComparisonResult)
  );
};

// ===== STATISTICS =====

export interface MarketplaceStats {
  totalSuppliers: number;
  totalServices: number;
  totalOrders: number;
  totalRevenue: number;
  averageRating: number;
  topCategories: Array<{
    category: ServiceCategory;
    count: number;
  }>;
  recentActivity: Array<{
    type: string;
    description: string;
    timestamp: Date;
  }>;
}

export const getMarketplaceStats = async (): Promise<MarketplaceStats> => {
  const { data } = await httpClient.get<
    ApiResponse<MarketplaceStats> | MarketplaceStats
  >(`${BASE_URL}/stats`);
  return (
    data && typeof data === "object" && "data" in data
      ? (data as ApiResponse<MarketplaceStats>).data
      : data
  ) as MarketplaceStats;
};

export interface SupplierStats {
  totalOrders: number;
  totalRevenue: number;
  averageRating: number;
  totalReviews: number;
  completionRate: number;
  responseTime: number;
  popularServices: Array<{
    serviceId: string;
    name: string;
    orders: number;
  }>;
  revenueByMonth: Array<{
    month: string;
    revenue: number;
  }>;
}

export const getSupplierStats = async (id: string): Promise<SupplierStats> => {
  const { data } = await httpClient.get<ApiResponse<SupplierStats>>(
    `${BASE_URL}/suppliers/${id}/stats`,
  );
  return data.data;
};

// ===== PAYMENTS =====

export interface PaymentIntent {
  id: string;
  bookingId?: string;
  orderId?: string;
  amount: number;
  currency: string;
  status:
    | "requires_payment_method"
    | "requires_confirmation"
    | "requires_action"
    | "processing"
    | "succeeded"
    | "canceled";
  clientSecret: string;
  createdAt: Date;
}

export interface Payment {
  id: string;
  userId: string;
  bookingId?: string;
  orderId?: string;
  amount: number;
  currency: string;
  status: "pending" | "completed" | "failed" | "refunded";
  paymentMethod: string;
  transactionId?: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Создание платежного намерения для booking или order.
 * Backend: POST /marketplace/payments/intent
 */
export const createPaymentIntent = async (params: {
  bookingId?: string;
  orderId?: string;
  currency?: string;
}): Promise<PaymentIntent> => {
  const { data } = await httpClient.post<PaymentIntent>(
    `${BASE_URL}/payments/intent`,
    params,
  );
  return extractApiData<PaymentIntent>(data);
};

/**
 * Подтверждение платежа после успешной оплаты.
 * Backend: POST /marketplace/payments/:id/confirm
 */
export const confirmPayment = async (
  paymentId: string,
  paymentMethodId?: string,
): Promise<Payment> => {
  const { data } = await httpClient.post<Payment>(
    `${BASE_URL}/payments/${paymentId}/confirm`,
    paymentMethodId ? { paymentMethodId } : {},
  );
  return extractApiData<Payment>(data);
};

/**
 * Получение истории платежей пользователя с пагинацией.
 * Backend: GET /marketplace/payments/history
 */
export interface PaymentHistoryParams {
  status?: "pending" | "completed" | "failed" | "refunded";
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface PaymentHistoryResponse {
  data: Payment[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const getPaymentHistory = async (
  params?: PaymentHistoryParams,
): Promise<PaymentHistoryResponse> => {
  const { data } = await httpClient.get<PaymentHistoryResponse>(
    `${BASE_URL}/payments/history`,
    { params },
  );
  return extractApiData<PaymentHistoryResponse>(data);
};

// ===== EXPORT =====

const marketplaceApi = {
  // Suppliers (5 endpoints)
  getSuppliers,
  createSupplier,
  getSupplier,
  updateSupplier,
  deleteSupplier,

  // Services (5 endpoints)
  getServices,
  createService,
  getService,
  updateService,
  deleteService,

  // Quotations (5 endpoints)
  getQuotations,
  createQuotation,
  getQuotation,
  updateQuotation,
  deleteQuotation,

  // Reviews (5 endpoints)
  getReviews,
  createReview,
  getReview,
  updateReview,
  deleteReview,
  replyToReview,

  // Orders (5 endpoints)
  getOrders,
  createOrder,
  getOrder,
  updateOrder,
  deleteOrder,

  // Bookings (5 endpoints)
  getBookings,
  createBooking,
  getBooking,
  updateBooking,
  deleteBooking,

  // Search & Compare (2 endpoints)
  searchMarketplace,
  compareItems,

  // Favorites (3 endpoints + suppliers list)
  getFavorites,
  getFavoriteSuppliers,
  addToFavorites,
  removeFromFavorites,
  removeSupplierFromFavorites,

  // Statistics (2 endpoints)
  getMarketplaceStats,
  getSupplierStats,

  // Payments (3 placeholder endpoints)
  createPaymentIntent,
  confirmPayment,
  getPaymentHistory,
};
export default marketplaceApi;

// Total: 42 endpoints (все реализованы)
